import GiftIcon from '@assets/icons/GiftIcon'
import { useAuth } from '@auth/useAuth'
import theme, { colors } from '@globals/themes/defaultTheme'
import { desktopQuery, tabletQuery } from '@globals/themes/themeConstants'
import { TenantMetadata } from '@kudos/http-client'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DRAWER_WIDTH } from '@nav/Nav.constants'
import { T } from '@transifex/react'
import React, { useEffect, useRef } from 'react'

export const TRIAL_BANNER_HEIGHT = 54

const alertStyle: SxProps = {
  backgroundColor: colors.bluePrimary,
  borderRadius: 0,
  height: '100%',
  border: 'unset',
}

const boxStyle: SxProps = {
  [desktopQuery]: {
    width: '100%',
    position: 'fixed',
    top: 0,
    left: DRAWER_WIDTH,
    zIndex: theme.zIndex.appBar + 1,
  },
}

type TrialBannerProps = {
  metadata: TenantMetadata
  onHeightChange?: (height: number) => void
}

const TrialBanner: React.FC<TrialBannerProps> = ({ metadata, onHeightChange }) => {
  const isTablet = useMediaQuery(tabletQuery)
  const bannerRef = useRef<HTMLDivElement>(null)
  const { isTenantScope } = useAuth()

  useEffect(() => {
    if (bannerRef.current) {
      const height = bannerRef.current.offsetHeight
      onHeightChange?.(height)
    }
  }, [onHeightChange, metadata.trialDaysRemaining])

  useEffect(() => {
    const handleResize = () => {
      if (bannerRef.current) {
        const height = bannerRef.current.offsetHeight
        onHeightChange?.(height)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [onHeightChange])

  if (metadata.trialDaysRemaining === null || !isTenantScope) {
    return null
  }

  return (
    <Box ref={bannerRef} sx={boxStyle} id="trial-banner">
      <Stack sx={{ width: { xs: '100%', lg: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Alert sx={alertStyle} icon={<GiftIcon sx={{ color: `${colors.white} !important` }} />}>
          <Typography sx={{ color: colors.white }}>
            {!isTablet ? (
              <T
                _str="{trialDaysRemainingNum, plural, one {{boldText} - You have {trialDaysRemaining} day remaining. Need help or want to learn more? Reach out to your account representative.} other {{boldText} - You have {trialDaysRemaining} days remaining. Need help or want to learn more? Reach out to your account representative.}}"
                boldText={
                  <b>
                    <span>
                      <T _str="You are using a free trial of Kudos" />
                    </span>
                  </b>
                }
                trialDaysRemainingNum={metadata.trialDaysRemaining}
                trialDaysRemaining={<b>{metadata.trialDaysRemaining}</b>}
              />
            ) : (
              <T
                _str="{trialDaysRemainingNum, plural, one {You have {trialDaysRemaining} day left in your free trial} other {You have {trialDaysRemaining} days left in your free trial}}"
                trialDaysRemainingNum={metadata.trialDaysRemaining}
                trialDaysRemaining={<b>{metadata.trialDaysRemaining}</b>}
              />
            )}
          </Typography>
        </Alert>
      </Stack>
    </Box>
  )
}

export default TrialBanner
